/**
 * Navigation cancellation for edit-page unsaved-changes guards.
 *
 * Next.js pages router only treats thrown errors with `cancelled: true` as a
 * clean route abort (see buildCancellationError in next/router). Throwing a
 * bare string or a normal Error can surface as a runtime overlay or fail to
 * stop navigation reliably.
 */

export const NAVIGATION_CANCELLED_MESSAGE =
    "Route change aborted. Unsaved changes.";

export class NavigationCancelledError extends Error {
    readonly isNavigationCancelled = true;
    cancelled = true;

    constructor(message = NAVIGATION_CANCELLED_MESSAGE) {
        super(message);
        this.name = "NavigationCancelledError";
    }
}

/**
 * Abort the current Next.js pages-router navigation.
 * Callers should emit `routeChangeError` with this error before throwing.
 */
export function createNavigationCancelledError(): NavigationCancelledError {
    return new NavigationCancelledError();
}

export function abortRouteChange(): never {
    throw createNavigationCancelledError();
}

export function isNavigationCancelledError(error: unknown): boolean {
    if (typeof error === "string") {
        return (
            error === NAVIGATION_CANCELLED_MESSAGE ||
            error.includes("Route change aborted") ||
            error.includes("Abort route change") ||
            error.includes("Route Cancelled")
        );
    }

    if (!error || typeof error !== "object") {
        return false;
    }

    const err = error as {name?: string; message?: string; cancelled?: boolean; isNavigationCancelled?: boolean};
    if (err.isNavigationCancelled || err.cancelled === true) {
        return true;
    }

    return (
        error instanceof NavigationCancelledError ||
        err.name === "NavigationCancelledError" ||
        (typeof err.message === "string" &&
            (err.message.includes("Route change aborted") ||
                err.message.includes("Route Cancelled")))
    );
}
